import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  BusinessCategory,
  Lead,
  LeadCategory,
  OpeningTimes,
  translateCategory,
  translateUsageSpaceCategory,
  UsageSpace,
  UsageSpaceCategory,
  UsageSpaceInfo,
} from '../../lead.model';
import {
  ExaminationResult,
  getDefaultTariffs,
  ImageWithDescription,
  NotPossibleReason,
  PlaybackDeviceLocation,
  RoomExaminationResult,
  RoomExaminationResultInfo,
  SelfCreatedRoom,
  TooFarReason,
} from '../../examinationResult.model';
import { ErrorListService } from '../../reusable-components/error-list/error-list.service';
import { ExaminationResultService } from './lead-task-services/examination-result.service';
import {
  currentExaminationSettingsKey,
  SettingsService,
} from '../../services/indexed-db/object-stores/settings/settings.service';
import { LeadService } from '../../services/leads/lead.service';
import { SnackbarService } from '../../services/snackbar/snackbar.service';
import { LeadDashboardService } from '../lead-dashboard.service';
import {
  formatIncompletePhoneNumber,
  getCountryCallingCode,
  parsePhoneNumberWithError,
  PhoneNumber,
} from 'libphonenumber-js';

@Injectable()
export class LeadTaskService implements OnDestroy {
  static readonly MAX_TWO_PART_SAP_FIELD_LENGTH = 80;
  static readonly MAX_USAGE_SPACE_NAME_LENGTH = 80; // unsure whether same length is a coincidence

  private readonly lead: Lead;
  private readonly draft: ExaminationResult;

  // Tracks a running submission to block duplicate sends and drive the UI state
  private readonly submissionInProgress = new BehaviorSubject<boolean>(false);
  public readonly submissionInProgress$: Observable<boolean> =
    this.submissionInProgress.asObservable();

  public readonly errorListService: ErrorListService;
  public readonly errorListServiceDetailInformation: ErrorListService;
  public readonly errorListServiceRoomSelection: ErrorListService;
  public readonly errorListServiceGeneralInformation: ErrorListService;

  constructor(
    private readonly route: ActivatedRoute, // not used directly, but still needed (at least in the tests)
    private readonly examinationResultService: ExaminationResultService,
    private readonly settingsService: SettingsService,
    private readonly leadService: LeadService,
    private readonly snackbarService: SnackbarService,
    private readonly leadDashboardService: LeadDashboardService,
  ) {
    this.lead = this.leadDashboardService.getCurrentLead();
    this.draft = this.leadDashboardService.getCurrentDraft();
    if (
      this.lead.usageSpaces.length === 1 &&
      this.draft.roomExaminations.length == 0
    ) {
      this.setCurrentRoomExaminationId(this.lead.usageSpaces[0].id);
    }
    this.errorListService = ErrorListService.create();
    this.errorListServiceDetailInformation =
      this.errorListService.create('detail-information');
    this.errorListServiceRoomSelection =
      this.errorListService.create('room-selection');
    this.errorListServiceGeneralInformation = this.errorListService.create(
      'general-information',
    );
    // TODO - when enabling the draft saving in the beginning here, the unit tests explode
    //this.updateCurrentDraft();
  }

  getCurrentRoomExaminationId(): string | undefined {
    return this.draft.currentRoomExaminationId;
  }

  setCurrentRoomExaminationId(id: string | undefined) {
    this.draft.currentRoomExaminationId = id;
  }

  sendDraftToBackend(
    latitude: number,
    longitude: number,
    reason: TooFarReason | undefined = undefined,
  ): void {
    if (this.submissionInProgress.value) {
      // A submission is already running; ignore repeated clicks
      return;
    }
    // LEER is only a placeholder and the backend rejects it - block the submit
    if (this.getLeadCategory() === 'LEER') {
      this.snackbarService.show(
        'components.detail-information.lead-category-needed-error',
        0,
      );
      return;
    }
    this.submissionInProgress.next(true);

    // Stable submission ID: generated once, kept in the persisted draft so retries reuse it
    this.draft.submissionId ??= crypto.randomUUID();

    // Persist a recoverable snapshot before the first network call
    this.settingsService
      .setLeadWithExaminationResult({
        lead: this.lead,
        examinationResult: this.draft,
      })
      .then(() => this.submitPersistedDraft(latitude, longitude, reason))
      .catch(() => {
        this.submissionInProgress.next(false);
        this.snackbarService.show(
          'components.examination-result.local-save-failure-snackbar',
          0,
        );
      });
  }

  private submitPersistedDraft(
    latitude: number,
    longitude: number,
    reason: TooFarReason | undefined,
  ): void {
    this.snackbarService.show('components.examination-result.pending-snackbar');

    this.examinationResultService
      .submitToBackend(
        this.buildSubmissionDraft(),
        latitude,
        longitude,
        reason,
      )
      .subscribe({
        next: () => this.handleSubmissionSuccess(),
        error: () => {
          // Keep draft, photos and selection so the user can retry
          this.submissionInProgress.next(false);
          this.snackbarService.show(
            'components.examination-result.failure-snackbar',
            0,
          );
        },
      });
  }

  // Submit-time normalization: the backend requires a lead category and a
  // concrete category for every reported room, so values are resolved here
  // instead of relying on a backend fallback to the master data.
  private buildSubmissionDraft(): ExaminationResult {
    return {
      ...this.draft,
      leadCategory: this.getLeadCategory(),
      roomExaminations: this.allCoveredRooms().map(
        (room: RoomExaminationResult) => this.withResolvedRoomDetails(room),
      ),
    };
  }

  // With a not-possible reason every usage space must reach SAP: rooms that were
  // not visited are added without findings. The persisted draft stays untouched,
  // so clearing the reason restores the usual room examination checks.
  private allCoveredRooms(): RoomExaminationResult[] {
    if (this.draft.notPossibleReason === undefined) {
      return this.draft.roomExaminations;
    }
    const coveredIds = new Set(
      this.draft.roomExaminations.map((room: RoomExaminationResult) => room.id),
    );
    const missingRooms: RoomExaminationResult[] = this.lead.usageSpaces
      .filter((usageSpace: UsageSpace) => !coveredIds.has(usageSpace.id))
      .map((usageSpace: UsageSpace) =>
        this.toUnvisitedRoomExamination(usageSpace),
      );
    return [...this.draft.roomExaminations, ...missingRooms];
  }

  // A room entry that carries no findings; the detail fields are filled by
  // withResolvedRoomDetails() at submit time.
  private toUnvisitedRoomExamination(
    usageSpace: UsageSpace,
  ): RoomExaminationResult {
    return {
      id: usageSpace.id,
      info: {
        tariffs: getDefaultTariffs(),
        images: [],
        audioUrls: [],
        roomIsClosed: false,
      },
    };
  }

  // Fills missing room details from the master data. Self-created rooms live
  // in the draft, not in lead.usageSpaces, so both lists are searched. Broken
  // values get the SAP-agreed placeholders (99 qm / AUSSEGNUNGSHALLE) so the
  // DTO validation passes; valid user input is never overwritten. Hotel rooms
  // legitimately carry size 0 + numberRooms and never get a size placeholder.
  private withResolvedRoomDetails(
    room: RoomExaminationResult,
  ): RoomExaminationResult {
    if (room.info.roomIsClosed) {
      return room;
    }
    const masterInfo =
      this.lead.usageSpaces.find(
        (usageSpace: UsageSpace) => usageSpace.id === room.id,
      )?.info ??
      this.draft.selfCreatedRooms.find(
        (selfCreatedRoom: SelfCreatedRoom) =>
          selfCreatedRoom.selfAssignedId === room.id,
      )?.info;
    const category =
      this.validCategory(room.info.usageSpaceCategory) ??
      this.validCategory(masterInfo?.category) ??
      'AUSSEGNUNGSHALLE';
    return {
      ...room,
      info: {
        ...room.info,
        name:
          this.nonBlank(room.info.name) ??
          this.nonBlank(masterInfo?.name) ??
          'Nutzungsraum',
        size:
          this.validSize(room.info.size) ??
          this.validSize(masterInfo?.size) ??
          (category === 'HOTEL_GAESTEZIMMER' ? undefined : 99),
        usageSpaceCategory: category,
      },
    };
  }

  private validCategory(
    category: UsageSpaceCategory | null | undefined,
  ): UsageSpaceCategory | undefined {
    return category && category !== 'UNBEKANNT' ? category : undefined;
  }

  private validSize(size: number | null | undefined): number | undefined {
    return size != null && size >= 1 ? size : undefined;
  }

  private nonBlank(value: string | null | undefined): string | undefined {
    return value && value.trim() !== '' ? value : undefined;
  }

  private handleSubmissionSuccess(): void {
    this.leadService.removeLead(this.lead);
    void this.settingsService.deleteSetting(currentExaminationSettingsKey);
    this.submissionInProgress.next(false);
    this.snackbarService.show('components.examination-result.success-snackbar');
    this.leadDashboardService.finishExamination();
  }

  private updateCurrentDraft() {
    this.settingsService.setLeadWithExaminationResult({
      lead: this.lead,
      examinationResult: this.draft,
    });
  }

  public addOrUpdateRoomExaminationResult(
    roomExaminationResult: RoomExaminationResult,
  ) {
    this.deleteRoomExaminationResult(roomExaminationResult.id);
    this.draft.roomExaminations.push(
      JSON.parse(JSON.stringify(roomExaminationResult)),
    );
    this.updateCurrentDraft();
  }

  public deleteRoomExaminationResult(id: string): void {
    this.draft.roomExaminations = this.draft.roomExaminations.filter(
      (roomExamination: RoomExaminationResult) => roomExamination.id != id,
    );
  }

  public removeRoomExaminationResult(id: string): void {
    this.draft.roomExaminations = this.draft.roomExaminations.filter(
      (roomExamination: RoomExaminationResult) => roomExamination.id != id,
    );
    this.updateCurrentDraft();
  }

  /**
   * now getter and setter for detail variables
   */

  public getNotes(): string {
    return this.draft.notes ? this.draft.notes : '';
  }

  public setNotes(notes: string): void {
    this.draft.notes = notes;
    this.updateCurrentDraft();
  }

  public getImages(): string[] {
    return this.draft.images.map(
      (imageWithDescription: ImageWithDescription) =>
        imageWithDescription.image,
    );
  }

  public addImage(image: string): void {
    this.draft.images.push({image: image});
    this.updateCurrentDraft();
  }

  public removeImageWithDescription(index: number): void {
    this.draft.images.splice(index, 1);
    this.updateCurrentDraft();
  }

  public addDescription(index: number, description: string): void {
    this.draft.images[index].description = description;
    this.updateCurrentDraft();
  }

  public getDescription(index: number): string {
    return this.draft.images[index].description || '';
  }

  public getAddress(): string {
    if (this.lead.address.street.length === 0) {
      return (
        this.lead.address.postalCode +
        ' ' +
        this.lead.address.city +
        ' ' +
        this.lead.address.district
      );
    }
    return (
      this.lead.address.street +
      ' ' +
      this.lead.address.houseNumber +
      ', ' +
      this.lead.address.postalCode +
      ' ' +
      this.lead.address.city +
      ' ' +
      this.lead.address.district
    );
  }

  public getLeadCategory(): LeadCategory {
    return this.draft.leadCategory
      ? this.draft.leadCategory
      : this.lead.leadCategory;
  }

  public getBusinessCategory(): BusinessCategory {
    return this.lead.businessCategory;
  }

  public getTranslatableLeadCategory(): string {
    return translateCategory(
      this.draft.leadCategory
        ? this.draft.leadCategory
        : this.lead.leadCategory,
    );
  }

  public setLeadCategory(leadCategory: LeadCategory): void {
    this.draft.leadCategory = leadCategory;
  }

  public getName(): string {
    return this.draft.name !== undefined
      ? this.draft.name
      : this.lead.name || '';
  }

  public setName(name: string): void {
    this.draft.name = name;
  }

  public validateTwoPartSAPField(field: string): boolean {
    return field.length <= LeadTaskService.MAX_TWO_PART_SAP_FIELD_LENGTH;
  }

  public getOperatorName(): string {
    if (this.draft.operatorName !== undefined) {
      return this.draft.operatorName;
    }
    return this.lead.operatorName || '';
  }

  public setOperatorName(name: string) {
    if (this.validateTwoPartSAPField(name)) {
      this.draft.operatorName = name;
      this.updateCurrentDraft();
    } else {
      console.error('try to set invalid operatorName:' + name);
    }
  }

  public validateTel(tel: string): boolean {
    if (tel === undefined || tel === '') {
      return false; // empty phone number is not valid
    }

    return /^(?!\s*$)\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}?[-.\s]?\d{1,9}?$/.test(
      tel,
    );
  }

  public getTel(): string {
    const draftValue = this.draft.tel;
    if (draftValue !== undefined && draftValue !== '') {
      return draftValue;
    }
    if (
      this.lead.tel !== null &&
      this.lead.tel !== undefined &&
      this.lead.tel !== ''
    ) {
      return this.lead.tel;
    }
    return '+' + getCountryCallingCode('DE');
  }

  public setTel(tel: string): void {
    if (this.validateTel(tel)) {
      // valid phone number
      this.draft.tel = tel;
      this.updateCurrentDraft();
    } else if (tel === '+' + getCountryCallingCode('DE')) {
      // special case for default value, shall not be saved
      this.draft.tel = undefined;
      this.updateCurrentDraft();
    } else {
      console.error('Try to set invalid tel to Draft');
    }
  }

  public validateWebsite(website: string): boolean {
    return (
      // \p{L} instead of a-z so umlaut/IDN hostnames (e.g. "dönerwetter.de") pass too
      /^(?!\s*$)(https?:\/\/)?([\p{L}\d.-]+)\.([\p{L}.]{2,6})(\/[\w .-]*)*\/?$/u.test(
        website,
      ) && website.length <= 132
    ); // max length in SAP
  }

  public getWebsite(): string {
    const draftValue = this.draft.website;
    return draftValue || this.lead.website || '';
  }

  public setWebsite(website: string): void {
    if (this.validateWebsite(website)) {
      this.draft.website = website;
      this.updateCurrentDraft();
    } else {
      console.error('Try to set inalid website to Draft');
    }
  }

  public getOpeningTimes(): OpeningTimes {
    const draftValue = this.draft.openingTimes;
    if (draftValue !== undefined) {
      return draftValue;
    }
    return this.lead.openingTimes || {openingDays: []};
  }

  public setOpeningTimes(openingTimes: OpeningTimes): void {
    this.draft.openingTimes = openingTimes;
    this.updateCurrentDraft();
  }

  public addSelfCreatedRoomInfo(selfCreatedRoomInfo: UsageSpaceInfo): string {
    const ids: string[] = this.getUsageSpaceIds();
    const selfAssignedIds: string[] = this.getSelfCreatedRoomsIds();
    const allIds = ids.concat(selfAssignedIds);
    let i = 1;
    const getId = (number: number) => 'selfCreatedRoom_' + number.toString();
    while (allIds.includes(getId(i))) {
      i++;
    }
    const newId = getId(i);
    this.draft.selfCreatedRooms.push({
      selfAssignedId: newId,
      info: selfCreatedRoomInfo,
    });
    this.updateCurrentDraft();
    return newId;
  }

  public deleteSelfCreatedRoom(id: string): void {
    const room = this.draft.selfCreatedRooms.find(
      (selfCreatedRoom) => selfCreatedRoom.selfAssignedId == id,
    );
    if (room === undefined) {
      throw new Error('try to delete room whose id is not existent');
    }
    this.draft.selfCreatedRooms = this.draft.selfCreatedRooms.filter(
      (selfCreatedRoom) => selfCreatedRoom.selfAssignedId !== id,
    );
    this.updateCurrentDraft();
  }

  public setNotPossibleReason(
    reason: NotPossibleReason | undefined = undefined,
  ) {
    this.draft.notPossibleReason = reason;
    if (reason !== undefined) {
      this.errorListServiceRoomSelection.removeAllErrors(); // since this blocks further room examination,
      // all errors regarding rooms are obsolete
    }
    this.updateCurrentDraft();
  }

  /**
   * now getter and setter for usage spaces
   */

  public getUsageSpaceIds(): string[] {
    return this.lead.usageSpaces.map((usageSpace: UsageSpace) => usageSpace.id);
  }

  public getSelfCreatedRoomsIds(): string[] {
    return this.draft.selfCreatedRooms.map(
      (selfCreatedRoom: SelfCreatedRoom) => selfCreatedRoom.selfAssignedId,
    );
  }

  private getUsageSpaceInfo(id: string): UsageSpaceInfo {
    const usageSpaces: UsageSpace[] = this.lead.usageSpaces.filter(
      (usageSpace: UsageSpace) => usageSpace.id === id,
    );
    const selfCreatedRooms = this.draft.selfCreatedRooms.filter(
      (selfCreatedRoom) => selfCreatedRoom.selfAssignedId == id,
    );
    if (usageSpaces.length + selfCreatedRooms.length != 1) {
      throw new Error(
        'Did not find a unique usageSpace with ID:' +
        id +
        ', of lead with ID: ' +
        this.lead.id,
      );
    }
    return usageSpaces.length == 1
      ? usageSpaces[0].info
      : selfCreatedRooms[0].info;
  }

  public getUsageSpaceCopy(id: string): UsageSpaceInfo {
    const usageSpaceInfo = this.getUsageSpaceInfo(id);
    return JSON.parse(JSON.stringify(usageSpaceInfo));
  }

  public hasRoomDraft(id: string): boolean {
    return this.getRoomDraft(id) !== undefined;
  }

  public validateUsageSpaceSize(size: string): boolean {
    return /^([1-9]\d*)$/.test(size);
  }

  public validateUsageSpaceName(name: string): boolean {
    return (
      name.length > 0 &&
      name.length <= LeadTaskService.MAX_USAGE_SPACE_NAME_LENGTH
    );
  }

  public getUsageSpaceName(id: string): string {
    const roomDraft = this.getRoomDraft(id);
    if (roomDraft?.name !== undefined) {
      return roomDraft.name;
    }
    return this.getUsageSpaceInfo(id).name;
  }

  public validateUsageSpaceNumberRooms(numberRooms: string): boolean {
    return /^([1-9]\d*)$/.test(numberRooms);
  }

  public getUsageSpaceTranslatableCategory(id: string): string {
    const roomDraft = this.getRoomDraft(id);
    let category: UsageSpaceCategory = this.getUsageSpaceInfo(id).category ?? 'UNBEKANNT';
    if (roomDraft?.usageSpaceCategory) {
      category = roomDraft.usageSpaceCategory;
    }
    return translateUsageSpaceCategory(category);
  }

  private getRoomDraft(id: string): RoomExaminationResultInfo | undefined {
    return this.draft.roomExaminations.find(
      (roomExamination: RoomExaminationResult) => roomExamination.id === id,
    )?.info;
  }

  public getRoomDraftCopy(id: string): RoomExaminationResultInfo | undefined {
    const roomDraft = this.getRoomDraft(id);
    if (roomDraft === undefined) {
      return undefined;
    } else {
      return JSON.parse(JSON.stringify(roomDraft));
    }
  }

  /**
   * Takes the input from the input dialog and formats it to the international phone number format.
   * If the input starts with '0', it replaces it with '+49' (German country code).
   * If the input does not start with '+', it adds '+49' as default.
   * It also removes all non-digit characters except the leading '+'.
   * If the input is empty after cleaning, it returns an empty string.
   * @param value
   */
  public formatPhoneNumber(value: string): string {
    // Remove all non-digit characters except the leading '+'
    let cleanedNumber = value.replaceAll(/[^\d+]/g, '');

    // Handle empty string after cleaning
    if (!cleanedNumber) {
      return '';
    }

    // If the number starts with '0', replace with '+49' (German country code)
    if (cleanedNumber.startsWith('0')) {
      cleanedNumber =
        '+' + getCountryCallingCode('DE') + cleanedNumber.substring(1);
    }

    // If the number doesn't start with '+', add '+49' as default
    if (!cleanedNumber.startsWith('+')) {
      cleanedNumber = '+' + getCountryCallingCode('DE') + cleanedNumber;
    }

    try {
      const phone: PhoneNumber = parsePhoneNumberWithError(cleanedNumber, 'DE');
      cleanedNumber = formatIncompletePhoneNumber(cleanedNumber, phone.country);
      return cleanedNumber;
    } catch (error) {
      console.error(error);
      // If parsing fails, return the unprocessed phone number
      return cleanedNumber;
    }
  }

  ngOnDestroy(): void {
    void this.settingsService.deleteSetting(currentExaminationSettingsKey);
  }

  public getPlaybackDevicesVisible(): boolean {
    return this.draft.playbackDevicesVisible ?? false;
  }

  public setPlaybackDevicesVisible(value: boolean): void {
    this.draft.playbackDevicesVisible = value;
  }

  public getPlaybackDevicesLocation(): PlaybackDeviceLocation | null {
    return this.draft.playbackDevicesLocation ?? null;
  }

  public setPlaybackDevicesLocation(value: PlaybackDeviceLocation | null): void {
    this.draft.playbackDevicesLocation = value;
  }

  public getGuestsPresent(): boolean {
    return this.draft.guestsPresent ?? false;
  }

  public setGuestsPresent(value: boolean): void {
    this.draft.guestsPresent = value;
  }

  public getGuestCount(): number | null {
    return this.draft.guestCount ?? null;
  }

  public setGuestCount(value: number | null): void {
    this.draft.guestCount = value;
  }
}
